import {
  Components,
  IconButton,
  IconButtonProps,
  StepIconProps,
  Theme,
} from '@mui/material'
import { forwardRef } from 'react'
import { Link, LinkProps } from 'react-router-dom'
import palette from './palette'
import type {} from '@mui/x-data-grid/themeAugmentation'
import type {} from '@mui/lab/themeAugmentation'
import { getRowClickStyles } from 'shared/constants/clickableRows'
import {
  ArrowLeft,
  ArrowRight,
  DatePickerCalendar,
  ArrowDown,
} from 'shared/assets/globalworth'
/**
 * This maps Material-UI's link behavior to React Router.
 */
const RouterLink = forwardRef<
  any,
  Omit<LinkProps, 'to'> & { href: LinkProps['to'] }
>(({ href, ...props }, ref) => <Link ref={ref} to={href} {...props} />)

const StepLabel = ({ icon, completed, active }: StepIconProps) => {
  return (
    <div
      style={{
        color: palette.primary.contrastText,
        backgroundColor:
          completed || active ? palette.primary.main : palette.grey[300],
        width: '1.5rem',
        height: '1.5rem',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {icon}
    </div>
  )
}

const UncheckedCheckbox = () => (
  <div
    style={{
      width: '1rem',
      height: '1rem',
      margin: 4,
      borderStyle: 'solid',
      borderColor: palette.primary.main,
      borderWidth: 1,
    }}
  />
)

const CheckedCheckbox = () => (
  <div
    style={{
      width: '1rem',
      height: '1rem',
      margin: 4,
      borderStyle: 'solid',
      borderColor: palette.primary.main,
      borderWidth: 1,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}
  >
    <div
      style={{
        width: '0.75rem',
        height: '0.75rem',
        backgroundColor: palette.primary.main,
      }}
    />
  </div>
)

const UncheckedRadio = () => (
  <div
    style={{
      width: '1rem',
      height: '1rem',
      margin: 4,
      borderStyle: 'solid',
      borderColor: palette.primary.main,
      borderWidth: 1,
      borderRadius: '50%',
    }}
  />
)

const CheckedRadio = () => (
  <div
    style={{
      width: '1rem',
      height: '1rem',
      margin: 4,
      borderStyle: 'solid',
      borderColor: palette.primary.main,
      borderWidth: 1,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '50%',
    }}
  >
    <div
      style={{
        width: '0.75rem',
        height: '0.75rem',
        backgroundColor: palette.primary.main,
        borderRadius: '50%',
      }}
    />
  </div>
)

const LeftButton = (props: IconButtonProps) => (
  <IconButton {...props}>
    <ArrowLeft
      style={{
        height: 24,
        width: 24,
        padding: 4,
        color: props.disabled ? 'inherit' : palette.primary.main,
      }}
    />
  </IconButton>
)

const RightButton = (props: IconButtonProps) => (
  <IconButton {...props}>
    <ArrowRight
      style={{
        height: 24,
        width: 24,
        padding: 4,
        color: props.disabled ? 'inherit' : palette.primary.main,
      }}
    />
  </IconButton>
)

const SwitchViewButton = (props: IconButtonProps) => (
  <IconButton {...props}>
    <ArrowDown
      style={{
        height: 24,
        width: 24,
        padding: 4,
        color: props.disabled ? 'inherit' : palette.primary.main,
      }}
    />
  </IconButton>
)

const overrides: Components<Omit<Theme, 'components'>> = {
  MuiButton: {
    defaultProps: {
      fullWidth: true,
    },
  },
  MuiDataGrid: {
    styleOverrides: {
      root: {
        '& .MuiDataGrid-overlay': {
          fontSize: '1.25rem',
        },
        '& .MuiDataGrid-columnSeparator': {
          display: 'none',
        },
        ...getRowClickStyles(palette.primary.main),
      },
    },
  },
  MuiLink: {
    defaultProps: {
      // @ts-ignore
      component: RouterLink,
      underline: 'hover',
    },
  },
  MuiTextField: {
    defaultProps: {
      fullWidth: true,
    },
  },
  MuiTabs: {
    defaultProps: {
      sx: {
        //borderBottomStyle: 'solid',
        //borderBottomColor: palette.grey[300],
        //borderBottomWidth: 1,
        //marginBottom: 2.5,
      },
    },
  },
  MuiDrawer: {
    defaultProps: {
      PaperProps: {
        sx: {
          border: `1px solid ${palette.primary.main}`,
        },
      },
    },
  },
  MuiStepLabel: {
    defaultProps: {
      StepIconComponent: props => <StepLabel {...props} />,
    },
  },
  MuiCheckbox: {
    defaultProps: {
      icon: <UncheckedCheckbox />,
      checkedIcon: <CheckedCheckbox />,
      sx: {
        borderRadius: 0,
      },
    },
  },
  MuiRadio: {
    defaultProps: {
      icon: <UncheckedRadio />,
      checkedIcon: <CheckedRadio />,
    },
  },
  MuiDialog: {
    defaultProps: {
      PaperProps: {
        sx: {
          borderRadius: 0,
          padding: '6px',
        },
      },
    },
  },
  MuiDatePicker: {
    defaultProps: {
      components: {
        OpenPickerIcon: DatePickerCalendar,
        RightArrowIcon: ArrowRight,
        LeftArrowButton: LeftButton,
        RightArrowButton: RightButton,
        SwitchViewButton: SwitchViewButton,
      },
    },
  },
  MuiIconButton: {
    defaultProps: {
      sx: {
        '.MuiTouchRipple-child': {
          borderRadius: 0,
        },
        borderRadius: 0,
      },
    },
  },
  MuiSwitch: {
    styleOverrides: {
      root: {
        width: 55,
        height: 26,
        padding: 0,
        margin: 8,
        '& .MuiSwitch-switchBase': {
          padding: 0,
          margin: 2,
          transitionDuration: '300ms',
          '&.Mui-checked': {
            transform: 'translateX(28px)',
            color: '#fff',

            '& + .MuiSwitch-track': {
              backgroundColor: 'primary.main',
              opacity: 1,
              border: 0,
            },
          },
          '&.Mui-disabled .MuiSwitch-thumb': {
            color: '#ccc',
          },
          '&.Mui-disabled + .MuiSwitch-track': {
            opacity: 0.5,
          },
        },
        '& .MuiSwitch-thumb': {
          boxSizing: 'border-box',
          width: 22,
          height: 22,
          borderRadius: '0px',
        },
        '& .MuiSwitch-track': {
          backgroundColor: '#E9E9EA',
          opacity: 1,
          transition: 'background-color 500ms',
          borderRadius: '0px',
        },
      },
    },
  },
  MuiStepConnector: {
    styleOverrides: {
      line: {
        width: '85px',
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        '& svg': {
          color: theme.palette.primary.main,
        },
      }),
    },
  },
}

export default overrides
